<script setup lang="ts">
import type { ComboGroupSuggestion, ComboProduct, ComboSize, ComboSuggestion } from "@innoclouds/api";
import type { AxiosError } from "axios";
import { useTranslation } from "i18next-vue";
import { storeToRefs } from "pinia";
import { computed, nextTick, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import ArrowLeft from "#/assets/iconsDynamic/ArrowLeft.vue";
import PlusWhite from "#/assets/iconsDynamic/PlusWhite.vue";
import { createComboSelection, useComboModifiers } from "#/hooks/useComboModifiers/useComboModifiers";
import { useComboPrice } from "#/hooks/useComboPrice/useComboPrice";
import { useComboValidation } from "#/hooks/useComboValidation/useComboValidation";
import { useFlyAnimation } from "#/hooks/useFlyAnimation/useFlyAnimation";
import { useHorizontalOverflow } from "#/hooks/useHorizontalOverflow/useHorizontalOverflow";
import { InitConnection } from "#/requests";
import ModifiersCombo from "#/semantics/combo/modifiersCombo/ModifiersCombo.vue";
import { useCardStore } from "#/store/card";
import { useDecorationStore } from "#/store/decoration";
import { useMainStore } from "#/store/main";
import { useModulesStore } from "#/store/modules";
import { useThemeStore } from "#/store/theme";
import type { CalcComboResponse, CalcItem, ProductSelection } from "#/types/iCombo";
import ButtonDefault from "#/ui/buttons/default/ButtonDefault.vue";
import NotificationWindow from "#/ui/notificationWindow/NotificationWindow.vue";
import SizePillSelector from "#/ui/sizePillSelector/SizePillSelector.vue";
import { cloneDeep } from "#/utils/clone";
import { getLocaleName } from "#/utils/i18next";
import { applySelectedSizeToModifier } from "#/utils/syncModifierWithSize";
import CardMain from "../cardMain/CardMain.vue";

const props = defineProps<{
	visible: boolean;
	qr_menu?: boolean;
	comboData?: ComboSuggestion;
}>();

const emit = defineEmits<{
	(e: "update:visible", value: boolean): void;
	(e: "close-popup"): void;
	(e: "close-all"): void;
	(e: "close-product"): void;
}>();

const router = useRouter();
const route = useRoute();
const { t, i18next } = useTranslation();

const cardStore = useCardStore();
const decor = useDecorationStore().screen("main");
const mainStore = useMainStore();
const themeStore = useThemeStore();
const modulesStore = useModulesStore();

const { is18Allowed, is_18_above_content, notificationData_lib } = storeToRefs(mainStore);

const isVisible = ref(props.visible);
const selectedGroup = ref<ComboGroupSuggestion | null>(null);
const selectedProduct = ref<ComboProduct | null>(null);
const isNotificationWindows = ref(false);

const selections = ref<Record<string, ProductSelection>>({});
const activeProductIds = ref<Record<string, string>>({});

const infoBody = ref<HTMLElement | null>(null);

const selKey = (groupId: string, productId: string) => `${groupId}-${productId}`;
const currentSelection = (groupId?: string | null): ProductSelection | null => {
	if (!groupId) return null;
	const pid = activeProductIds.value[groupId];
	return pid ? selections.value[selKey(groupId, pid)] || null : null;
};

const selectionSurcharge = (selection: ProductSelection | null): number | null => {
	if (!selection?.product) return null;
	const size = selection.size as any;
	if (size && size.price_modification !== null && size.price_modification !== undefined) return Number(size.price_modification) || 0;
	return Number((selection.product as any).price_modification ?? 0) || 0;
};

const productSurcharge = (groupId: string, product: ComboProduct): number | null => {
	const sel = selections.value[selKey(groupId, product.id)];
	if (sel) return selectionSurcharge(sel);
	return Number((product as any).price_modification ?? 0) || 0;
};

const combo = computed<ComboSuggestion | null>(() => props.comboData ?? cardStore.dataCombo?.[0] ?? null);
const comboName = computed(() => getLocaleName(combo.value, i18next.language) || t(`popup-combo.title`));
const comboId = computed(() => combo.value?.id || null);
const comboList = computed<ComboGroupSuggestion[]>(() => combo.value?.groups ?? []);

const currentSelectionRef = computed<ProductSelection | null>(() => (selectedGroup.value ? currentSelection(selectedGroup.value.id) : null));

const {
	modifierAmounts,
	singleModifiers,
	singleModifiersGroupMax,
	singleModifiersGroupSelected,
	groupedModifiers,
	getMaxAmount,
	getGroupCapacity,
	getCurrentGroupAmount,
	buildModifierData,
	makeAmountChangeHandler,
	handleModifierClick,
	makeGroupedModifierAddToCartHandler,
	repriceForSize,
	validateGroups,
} = useComboModifiers(currentSelectionRef as any);

const resolveProductSize = (product: ComboProduct | null, preferredId?: string): ComboSize | null => {
	const sizes = product?.sizes ?? [];
	if (!sizes.length) return null;
	return (preferredId && sizes.find((s) => s.id === preferredId)) || sizes.find((s) => s.is_default) || sizes[0] || null;
};

const switchToProduct = (groupId: string, product: ComboProduct) => {
	if (!product) return;
	activeProductIds.value[groupId] = product.id;

	const key = selKey(groupId, product.id);
	const selection = selections.value[key];

	if (!selection) {
		const size = resolveProductSize(product);
		selections.value[key] = createComboSelection(product, size ? cloneDeep(size) : null);
	} else {
		if (selection.size && !product.sizes.some((s) => s.id === selection.size!.id)) {
			const fallback = resolveProductSize(product);
			selection.size = fallback ? cloneDeep(fallback) : null;
			repriceForSize(selection.size);
		}
		selection.product = product;
	}

	selectedProduct.value = product;
};

const selectedGroupProducts = computed<ComboProduct[]>(() => selectedGroup.value?.products ?? []);

const {
	headerCardsContainer,
	productsCardsContainer,
	sizeListContainer,
	singleModifiersContainer,
	groupedModifiersContainers,
	isHeaderCardsOverflowing,
	isProductsCardsOverflowing,
	isSizeOverflowing,
	isSingleModifiersOverflowing,
	groupedModifiersOverflowing,
	updateOverflowStates,
} = useHorizontalOverflow([selectedGroupProducts, singleModifiers, groupedModifiers]);

const { productCardRefs, groupCardRefs, scrollProductIntoView, triggerFlyAnimation } = useFlyAnimation();

const comboSelectionsView = computed<Record<string, ProductSelection>>(() => {
	const result: Record<string, ProductSelection> = {};
	Object.keys(activeProductIds.value).forEach((gid) => {
		const sel = currentSelection(gid);
		if (sel) result[gid] = sel;
	});
	return result;
});

const { firstErrorGroupId, firstErrorProductId, validationErrorMessage, validateComboSelection } = useComboValidation(comboList, comboSelectionsView);

const { buttonText, calculateComboItemsTotal } = useComboPrice(comboSelectionsView, combo);

const selectInitialProduct = () => {
	const groups = comboList.value;
	if (!groups.length) return;

	const sourceProductId = cardStore.stateCard.openCard?.id ?? null;

	const preferredGroup =
		(sourceProductId && groups.find((g) => g.is_main && g.products.some((p) => p.id === sourceProductId))) ||
		(sourceProductId && groups.find((g) => g.products.some((p) => p.id === sourceProductId))) ||
		groups[0];

	const preferredProduct = (sourceProductId && preferredGroup.products.find((p) => p.id === sourceProductId)) || preferredGroup.products[0];

	if (!preferredProduct) return;

	selectedGroup.value = preferredGroup;
	switchToProduct(preferredGroup.id, preferredProduct);

	nextTick(() => {
		scrollProductIntoView(headerCardsContainer.value, groupCardRefs.value[preferredGroup.id]);
		scrollProductIntoView(productsCardsContainer.value, productCardRefs.value[preferredProduct.id]);
	});
};

const resetScrollPositions = () => {
	[
		headerCardsContainer.value,
		productsCardsContainer.value,
		sizeListContainer.value,
		singleModifiersContainer.value,
		...Object.values(groupedModifiersContainers.value),
	].forEach((el) => {
		if (el) el.scrollLeft = 0;
	});
	if (infoBody.value) infoBody.value.scrollTop = 0;
};

const showPopup = async () => {
	document.body.style.overflow = "hidden";

	await nextTick();
	selectInitialProduct();
	updateOverflowStates();

	await nextTick();
	resetScrollPositions();
	requestAnimationFrame(resetScrollPositions);
};

const hidePopup = () => {
	document.body.style.overflow = "";
	selectedProduct.value = null;
	selectedGroup.value = null;
	selections.value = {};
	activeProductIds.value = {};
	isVisible.value = false;
	emit("update:visible", false);
	emit("close-popup");
};

watch(
	() => props.visible,
	(newValue) => {
		if (newValue) {
			isVisible.value = true;
			showPopup();
		} else {
			hidePopup();
		}
	},
	{ immediate: true },
);

const goBack = () => hidePopup();
const goClose = () => {
	hidePopup();
	emit("close-all");
};

const validateCurrentGroup = (): boolean => {
	if (!selectedGroup.value) return true;
	const result = validateGroups();
	if (!result.valid) {
		validationErrorMessage.value = result.errorText || t(`popup-combo.validation.required-modifiers`);
		firstErrorGroupId.value = selectedGroup.value.id;
		return false;
	}
	return true;
};

const saveSelectedSize = (groupId: string, productId: string, size: ComboSize | null) => {
	const selection = selections.value[selKey(groupId, productId)];
	if (!selection || selection.product?.id !== productId) return;
	selection.size = size ? cloneDeep(size) : null;
	repriceForSize(selection.size);
};

const effectiveSize = (selection: ProductSelection): ComboSize | null => selection.size ?? resolveProductSize(selection.product);

const buildCalcItem = (selection: ProductSelection): CalcItem => {
	const size = effectiveSize(selection);
	const sizeId = size?.id ?? null;
	const item: CalcItem = {
		id: selection.product.id,
		amount: 1,
		size: sizeId,
		modifiers: selection.modifiers.map((m) => ({ id: m.id, size: sizeId, amount: m.amount || 1 })),
		group_modifiers: selection.groupModifiers.map((g) => ({
			group: g.id || g.group || "",
			modifiers: (g.modifiers ?? []).map((m) => ({ id: m.id, size: sizeId, amount: m.amount || 1 })),
		})),
	};
	return item;
};

const buildCartItem = (selection: ProductSelection) => {
	const { product, modifiers, groupModifiers } = selection;
	const size = effectiveSize(selection);
	const basePrice = Number(size?.price ?? product.price) || 0;
	const surcharge =
		size && size.price_modification !== null && size.price_modification !== undefined
			? Number(size.price_modification) || 0
			: Number(product.price_modification ?? 0) || 0;
	const applyMod = (m: any) => applySelectedSizeToModifier(m, size as any);
	return {
		id: product.id,
		name: product.name,
		loc_name: product.loc_name,
		amount: 1,
		size: size?.id ?? null,
		sizes: product.sizes,
		size_name: size?.name || size?.alt_name || null,
		price: basePrice,
		old_price: basePrice,
		price_modification: surcharge,
		modifiers: modifiers.map(applyMod),
		group_modifiers: groupModifiers.map((g) => ({ ...g, modifiers: (g.modifiers ?? []).map(applyMod) })),
	};
};

const NUTRITION_KEYS = ["energy_amount", "proteins_amount", "fat_amount", "carbohydrates_amount"] as const;

const handleAddToCart = async () => {
	if (!validateComboSelection()) {
		const targetGroupId = firstErrorGroupId.value;
		const targetProductId = firstErrorProductId.value;

		if (targetGroupId) {
			const targetGroup = comboList.value.find((g) => g.id === targetGroupId);
			if (targetGroup) {
				selectedGroup.value = targetGroup;
				const targetProduct = targetProductId ? targetGroup.products.find((p) => p.id === targetProductId) : currentSelection(targetGroupId)?.product;
				if (targetProduct) {
					switchToProduct(targetGroupId, targetProduct);
				} else {
					selectedProduct.value = null;
				}
				nextTick(() => {
					document.querySelector(`[data-combo-group-id="${targetGroup.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
				});
			}
		}
		if (validationErrorMessage.value) isNotificationWindows.value = true;
		return;
	}

	if (!comboId.value) {
		console.error("Combo ID not found");
		return;
	}

	const comboGroups: { id: string; item: CalcItem }[] = [];
	const comboGroupsForCart: { id: string; item: ReturnType<typeof buildCartItem> }[] = [];

	Object.keys(activeProductIds.value).forEach((groupId) => {
		const selection = currentSelection(groupId);
		if (!selection?.product) return;
		comboGroups.push({ id: groupId, item: buildCalcItem(selection) });
		comboGroupsForCart.push({ id: groupId, item: buildCartItem(selection) });
	});

	const calculatePayload = { items: [], combos: [{ id: comboId.value, amount: 1, groups: comboGroups, front_item_id: comboId.value }] };

	try {
		const connection = InitConnection.create();
		const response: CalcComboResponse = await connection.calculateCombo(calculatePayload);

		const comboResult: any = response?.combos?.[0] ?? response ?? {};
		const fallbackPrice = calculateComboItemsTotal();
		const comboPrice = Number(comboResult.total_combo_price ?? fallbackPrice) || fallbackPrice;

		const originalCombo = combo.value;
		const nutrition = Object.fromEntries(NUTRITION_KEYS.map((k) => [k, comboResult[k] ?? 0]));

		mainStore.addToCart({
			...originalCombo,
			id: comboId.value,
			name: combo.value?.name,
			loc_name: combo.value?.loc_name,
			amount: 1,
			price: comboPrice,
			old_price: comboPrice,
			discounts: comboResult.discounts ?? [],
			image_link: originalCombo?.image_link ?? "",
			combos: [{ id: comboId.value, amount: 1, groups: comboGroupsForCart }],
			...nutrition,
			is_combo: true,
			sizes: [],
		});

		hidePopup();
		emit("close-all");
		emit("close-product");
		if (props.qr_menu) router.push(`/${route.params.orgId}/`);
	} catch (error) {
		console.error("Ошибка расчета комбо:", error);
		const responseData = (error as AxiosError)?.response?.data as Record<string, any>;
		modulesStore.showNotification(responseData.user_friendly_message || t(`popup-combo.error.adding-to-cart`));
		if (responseData?.extra?.action === "RemoveOutOfStockItems") emit("close-all");
	}
};

const setGroupCardRef = (el: any, id: string) => {
	if (el && id) groupCardRefs.value[id] = el as HTMLElement;
};

const setProductCardRef = (el: any, id: string) => {
	if (el && id) productCardRefs.value[id] = el as HTMLElement;
};

const setGroupedModifiersContainerRef = (el: any, id: string) => {
	if (el && id) groupedModifiersContainers.value[id] = el as HTMLElement;
};

const onChooseSize = (size: any) => {
	if (selectedGroup.value && selectedProduct.value) {
		saveSelectedSize(selectedGroup.value.id, selectedProduct.value.id, size as any);
	}
};

const scrollToSelectedProduct = (productId: string) => {
	if (!productId) return;
	nextTick(() =>
		nextTick(() =>
			requestAnimationFrame(() => {
				const targetElement = productCardRefs.value[productId];
				const container = productsCardsContainer.value;
				if (!targetElement || !container) return;
				scrollProductIntoView(container, targetElement);
				setTimeout(() => {
					const elementRect = targetElement.getBoundingClientRect();
					const containerRect = container.getBoundingClientRect();
					const scrollLeft = targetElement.offsetLeft - containerRect.width / 2 + elementRect.width / 2;
					container.scrollTo({ left: Math.max(0, scrollLeft), behavior: "smooth" });
				}, 50);
			}),
		),
	);
};

const openGroup = (groupId: string) => {
	if (selectedGroup.value && groupId !== selectedGroup.value.id) {
		if (!validateCurrentGroup()) {
			isNotificationWindows.value = true;
			nextTick(() => {
				document.querySelector(`[data-combo-group-id="${selectedGroup.value?.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
			});
			return;
		}
	}

	const group = comboList.value.find((g) => g.id === groupId);
	if (!group) return;
	selectedGroup.value = group;
	nextTick(() => scrollProductIntoView(headerCardsContainer.value, groupCardRefs.value[group.id]));

	const savedSelection = currentSelection(group.id);
	if (savedSelection?.product) {
		switchToProduct(group.id, savedSelection.product);
		scrollToSelectedProduct(savedSelection.product.id);
	} else {
		selectedProduct.value = null;
	}
};

const openProductInGroup = (productId: string) => {
	if (!selectedGroup.value) return;
	const product = selectedGroup.value.products.find((p) => p.id === productId);
	if (!product) return;

	if ((product as any).adult && !is18Allowed.value) {
		is_18_above_content.value = true;
		notificationData_lib.value.callback = () => openProductInGroup(productId);
		return;
	}

	const oldProductId = selectedProduct.value?.id;
	const isChanged = oldProductId !== product.id;

	const groupId = selectedGroup.value.id;
	switchToProduct(groupId, product);
	scrollProductIntoView(productsCardsContainer.value, productCardRefs.value[product.id]);

	if (isChanged) {
		nextTick(() => {
			const productCard = productCardRefs.value[product.id];
			const currentGroupCard = groupCardRefs.value[selectedGroup.value!.id];
			if (productCard) triggerFlyAnimation(productCard, currentGroupCard);
		});
	}
};

watch(comboList, (newList) => {
	if (newList?.length && isVisible.value && !selectedGroup.value && !selectedProduct.value) {
		selectInitialProduct();
	}
	nextTick(updateOverflowStates);
});

watch([selectedGroupProducts, singleModifiers, groupedModifiers], () => nextTick(updateOverflowStates));
</script>

<template>
	<div class="popup_overlay_combo_add" v-show="isVisible">
		<div class="popup_combo_add_wrapper">
			<div class="drag_handle"></div>
			<div class="popup_combo_add_header">
				<div class="header_back" @click="goBack">
					<ArrowLeft class="back_icon" />
				</div>
				<p>{{ comboName }}</p>
				<div class="header_close_combo" @click="goClose">
					<PlusWhite class="close_icon" />
				</div>
			</div>
			<div class="popup_combo_add_info_header">
				<div class="combo_info">
					<p class="title">{{ t(`popup-combo.composition`) }}</p>
					<p class="info_combo">{{ t(`popup-combo.composition-info-1`) }}</p>
					<p class="info_combo">{{ t(`popup-combo.composition-info-2`) }}</p>
				</div>
				<div
					v-if="comboList.length > 0"
					ref="headerCardsContainer"
					class="popup_combo_cards_list"
					:class="{ 'popup_combo_cards_list_center': !isHeaderCardsOverflowing }"
				>
					<div
						v-for="(card) in comboList"
						:key="`group-${card.id || card.name}`"
						:ref="(el) => setGroupCardRef(el, card.id)"
						class="card"
						:class="{ 'card_selected_shadow': selectedGroup?.id === card.id }"
						:data-combo-group-id="card.id"
					>
						<CardMain
							:data="(currentSelection(card.id)?.product || card) as any"
							:activeTags="[]"
							:is_group="false"
							:parent="card.id"
							:isLoading="false"
							variant="combo"
							:comboSurcharge="selectionSurcharge(currentSelection(card.id))"
							:takeCurrentCard="() => openGroup(card.id)"
							decoration="main"
							v-bind="decor.bind('card')"
						/>
					</div>
				</div>
			</div>
			<div class="popup_combo_info_body" ref="infoBody" @touchmove.stop @pointerdown.stop>
				<div v-if="selectedGroup">
					<div class="combo_info">
						<p class="title">{{ t(`popup-combo.change-group`, { name: getLocaleName(selectedGroup, i18next.language) }) }}</p>
					</div>
					<div
						v-if="selectedGroupProducts.length > 0"
						ref="productsCardsContainer"
						class="popup_combo_cards_list"
						:class="{ 'popup_combo_cards_list_center': !isProductsCardsOverflowing }"
					>
						<div
							v-for="(product) in selectedGroupProducts"
							:key="`product-${product.id || product.name}`"
							:ref="(el) => setProductCardRef(el, product.id)"
							class="card"
							:class="{ 'card_selected_shadow': activeProductIds[selectedGroup.id] === product.id }"
						>
							<CardMain
								:data="(product as any)"
								:activeTags="[]"
								:is_group="false"
								:parent="product.id"
								:isLoading="false"
								variant="combo"
								:comboSurcharge="productSurcharge(selectedGroup.id, product)"
								:takeCurrentCard="() => openProductInGroup(product.id)"
								decoration="main"
								v-bind="decor.bind('card')"
							/>
						</div>
					</div>
				</div>
				<div v-if="selectedProduct?.sizes?.length">
					<div class="combo_info">
						<p class="title">{{ t(`popup-combo.change-size`) }}</p>
					</div>
					<div ref="sizeListContainer" class="popup_combo_size_list" :class="{ 'popup_combo_size_list_center': !isSizeOverflowing }">
						<SizePillSelector
							:hide-price="true"
							:sizes="(selectedProduct.sizes as any)"
							:selected="(currentSelection(selectedGroup?.id)?.size as any)"
							@choose="onChooseSize"
						/>
					</div>
				</div>
				<div v-if="singleModifiers.length > 0" :key="`single-mods-${selectedGroup?.id}-${selectedProduct?.id}`">
					<div class="combo_info">
						<p class="title">{{ t(`card.modifiers.additions`) }}</p>
						<p v-if="singleModifiersGroupMax > 0" class="combo_modifiers_count">
							{{ t(`popup-combo.selected-modifiers`, { selected: singleModifiersGroupSelected, max: singleModifiersGroupMax }) }}
						</p>
					</div>
					<div
						ref="singleModifiersContainer"
						class="popup_combo_modifiers_list popup_combo_modifiers_list_single"
						:class="{ 'popup_combo_modifiers_list_center': !isSingleModifiersOverflowing }"
						data-scrollable="true"
					>
						<ModifiersCombo
							v-for="item in singleModifiers"
							:key="`${item.group.id}-${item.modifier.id}`"
							:data="buildModifierData(item.modifier, item.group.id) as any"
							:group-id="item.group.id"
							:is_group="false"
							:is_product="true"
							:amount="modifierAmounts[`${item.group.id}-${item.modifier.id}`] ?? item.modifier.amount ?? 0"
							:max-amount="getMaxAmount(item.modifier, item.group)"
							:group-max-amount="getGroupCapacity(item.group)"
							:current-group-amount="getCurrentGroupAmount(item.group.id, item.modifier.id)"
							:on-amount-change="makeAmountChangeHandler(item.group.id, item.modifier.id)"
							:onAddToCart="() => handleModifierClick(item.group, item.groupIndex, item.modifier)"
							:parent-size="currentSelection(selectedGroup?.id)?.size"
							class="popup_combo_modifier_card"
						/>
					</div>
				</div>
				<div v-if="groupedModifiers.length > 0" :key="`group-mods-${selectedGroup?.id}-${selectedProduct?.id}`">
					<template v-for="groupedGroup in groupedModifiers" :key="groupedGroup.group.id">
						<div class="combo_info">
							<p class="title">{{ getLocaleName(groupedGroup.group, i18next.language) }}</p>
							<p v-if="groupedGroup.maxAmount > 0" class="combo_modifiers_count">
								{{ t(`popup-combo.selected-modifiers`, { selected: groupedGroup.selectedAmount, max: groupedGroup.maxAmount }) }}
							</p>
						</div>
						<div
							class="popup_combo_modifiers_list popup_combo_modifiers_list_single"
							:ref="(el) => setGroupedModifiersContainerRef(el, groupedGroup.group.id)"
							:class="{ 'popup_combo_modifiers_list_center': !groupedModifiersOverflowing[groupedGroup.group.id] }"
						>
							<ModifiersCombo
								v-for="modifier in groupedGroup.modifiers"
								:key="`${groupedGroup.group.id}-${modifier.id}`"
								:data="buildModifierData(modifier, groupedGroup.group.id) as any"
								:group-id="groupedGroup.group.id"
								:is_group="false"
								:is_product="true"
								:amount="modifierAmounts[`${groupedGroup.group.id}-${modifier.id}`] ?? modifier.amount ?? 0"
								:max-amount="getMaxAmount(modifier, groupedGroup.group)"
								:group-max-amount="getGroupCapacity(groupedGroup.group)"
								:current-group-amount="getCurrentGroupAmount(groupedGroup.group.id, modifier.id)"
								:on-amount-change="makeAmountChangeHandler(groupedGroup.group.id, modifier.id)"
								:onAddToCart="makeGroupedModifierAddToCartHandler(groupedGroup, modifier)"
								class="popup_combo_modifier_card"
							/>
						</div>
					</template>
				</div>
			</div>
		</div>
		<div class="popup_combo_add_footer">
			<ButtonDefault :text-button-default="buttonText" :on-click="handleAddToCart" />
		</div>
		<NotificationWindow
			:visible="isNotificationWindows"
			:textNotificationDefault="validationErrorMessage"
			:backgroundColor="themeStore.data?.accent_color || '#FF6D2E'"
			:buttonAccept="null"
			@update:visible="isNotificationWindows = $event"
		/>
	</div>
</template>

<style lang="scss">
  @use './popupComboAdd.scss'
</style>
